import { useMemo } from "react";

import { Box, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { type EChartsOption } from "echarts";
import ECharts from "echarts-for-react";

import { useDarkThemeCheck } from "../../../../../Components/hooks/useDarkThemeCheck";
import { type K8sUtilizationMetric } from "../../types";
import { ChartId } from "../ClusterDetail";
import ChartCard from "./ChartCard";
import { formatCpu, formatMemory, formatTimestamp, formatTimestampAxis, getCommonChartOptions } from "./utils";

const areaStyle = {
  opacity: 0.5,
};
const xAxisIntervals = 4;

const chartStyles: React.CSSProperties = {
  height: "100%",
  width: "100%",
};

interface UtilizationChartsProps {
  metrics: K8sUtilizationMetric[];
  loading?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getEventHandlers?: (chartId: ChartId) => Record<string, (event: any) => void>;
  memoryChartRef?: React.RefObject<ECharts>;
  cpuChartRef?: React.RefObject<ECharts>;
}

const UtilizationCharts = ({
  metrics: originalMetrics,
  loading,
  getEventHandlers,
  memoryChartRef,
  cpuChartRef,
}: UtilizationChartsProps) => {
  const theme = useTheme();
  const isDarkMode = useDarkThemeCheck();

  const metrics = useMemo(
    () =>
      originalMetrics.map((m) => ({
        ...m,
        formatted_start_time: formatTimestamp(m.start_time),
      })),
    [originalMetrics]
  );

  const cpuChartOption: EChartsOption = {
    ...getCommonChartOptions(theme, {
      tooltip: {
        valueFormatter(value) {
          return formatCpu(String(value));
        },
      },
      yAxis: {
        axisLabel: {
          formatter: (value) => formatCpu(String(value)),
        },
      },
    }),
    xAxis: {
      data: metrics.map((m) => m.formatted_start_time),
      axisLabel: {
        formatter: formatTimestampAxis,
        interval: Math.floor(metrics.length / xAxisIntervals),
      },
    },
    series: [
      {
        name: "Allocated",
        type: "line",
        data: metrics.map((m) => m.cpu_limit),
        color: isDarkMode ? "#FFFFFF" : "#707070",
        showSymbol: false,
        areaStyle,
      },
      {
        name: "Requested",
        type: "line",
        data: metrics.map((m) => m.cpu_requested),
        color: isDarkMode ? "#1AB4FF" : "#0090d6",
        showSymbol: false,
        areaStyle,
      },
      {
        name: "Used",
        type: "line",
        data: metrics.map((m) => m.cpu_use),
        color: isDarkMode ? "#B2E9F0" : "#94E1EB",
        showSymbol: false,
        areaStyle,
      },
    ],
  };

  const memoryChartOption: EChartsOption = {
    ...getCommonChartOptions(theme, {
      tooltip: {
        valueFormatter(value) {
          return value ? formatMemory(String(value)) : "0";
        },
      },
      yAxis: {
        axisLabel: {
          formatter: (v) => formatMemory(v, 0),
        },
      },
    }),
    xAxis: {
      data: metrics.map((m) => m.formatted_start_time),
      axisLabel: {
        formatter: formatTimestampAxis,
        interval: Math.floor(metrics.length / xAxisIntervals),
      },
    },
    series: [
      {
        name: "Allocated",
        type: "line",
        data: metrics.map((m) => m.mem_limit),
        color: isDarkMode ? "#FFFFFF" : "#707070",
        showSymbol: false,
        areaStyle,
      },
      {
        name: "Requested",
        type: "line",
        data: metrics.map((m) => m.mem_requested),
        color: isDarkMode ? "#8D24A8" : "#461264",
        showSymbol: false,
        areaStyle,
      },
      {
        name: "Used",
        type: "line",
        data: metrics.map((m) => m.mem_use),
        color: "#E4A3F5",
        showSymbol: false,
        areaStyle,
      },
    ],
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <ChartCard title="Cluster Memory utilization" loading={loading}>
            <ECharts
              style={chartStyles}
              option={memoryChartOption}
              ref={memoryChartRef}
              onEvents={getEventHandlers?.(ChartId.MEMORY)}
              lazyUpdate
            />
          </ChartCard>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <ChartCard title="Cluster CPU utilization" loading={loading}>
            <ECharts
              style={chartStyles}
              option={cpuChartOption}
              ref={cpuChartRef}
              onEvents={getEventHandlers?.(ChartId.CPU)}
              lazyUpdate
            />
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UtilizationCharts;
